import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  validateIdentifier,
  WIZARD_VALIDATION_MESSAGES,
} from "@/services/wizardValidation";

interface CustomServiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingKeys: string[];
  onSubmit: (key: string, label: string) => void;
}

/**
 * Lets admins add a TCC service key that isn't in our built-in list — useful
 * for new macOS releases that ship services we haven't catalogued yet.
 */
export const CustomServiceDialog = ({
  open,
  onOpenChange,
  existingKeys,
  onSubmit,
}: CustomServiceDialogProps) => {
  const [key, setKey] = useState("");
  const [label, setLabel] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setKey("");
      setLabel("");
      setError(null);
    }
  }, [open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedKey = key.trim();
    const trimmedLabel = label.trim();

    // Use the shared validation contract so the inline messages here are
    // identical to the App Configuration wizard's row-level errors.
    const keyErr = validateIdentifier(trimmedKey, { treatEmptyAsError: true });
    if (keyErr) {
      setError(keyErr);
      return;
    }
    if (existingKeys.includes(trimmedKey)) {
      setError(WIZARD_VALIDATION_MESSAGES.keyDuplicate);
      return;
    }
    onSubmit(trimmedKey, trimmedLabel || trimmedKey);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add custom TCC service</DialogTitle>
          <DialogDescription>
            Use this to manage a service Apple has shipped but isn't in the
            built-in list yet. The TCC key must match the identifier Apple
            documents for the service.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="custom-tcc-key" className="text-sm">
              TCC key <span className="text-destructive">*</span>
            </Label>
            <Input
              id="custom-tcc-key"
              value={key}
              onChange={(e) => {
                setKey(e.target.value);
                setError(null);
              }}
              placeholder="e.g. SystemPolicyNewService"
              className="mt-1 font-mono text-sm"
              autoFocus
            />
            <p className="text-xs text-muted-foreground mt-1">
              The exact identifier sent to macOS in the TCC payload's{" "}
              <code className="font-mono">Services</code> dict.
            </p>
          </div>
          <div>
            <Label htmlFor="custom-tcc-label" className="text-sm">
              Display label
            </Label>
            <Input
              id="custom-tcc-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Friendly name (optional)"
              className="mt-1 text-sm"
            />
          </div>
          {error && <p className="text-xs text-destructive">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit">Add service</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
